"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { TrialSignupDialog } from "@/components/billing/TrialSignupDialog";
import {
  canPerformActionFor,
  type UserState,
} from "@/lib/billing/derive-user-state";

// Re-export so existing consumers (e.g. TrialBanner) keep working.
export type { UserState };

/**
 * Discriminated union over the async lifecycle of the user-state fetch.
 *
 * Three variants — the provider always begins fetching on mount, so there
 * is no "idle" phase. After the effect resolves, exactly one of `ready` or
 * `error` is observable. `canPerformAction` is derived from `userState`
 * via `canPerformActionFor` at the point of use; it is NOT stored as a
 * separate cell.
 */
export type ActionGateStatus =
  | { status: "loading" }
  | { status: "ready"; userState: UserState }
  | { status: "error" };

interface ActionGateContextValue {
  /** Async status of the user-state fetch. */
  state: ActionGateStatus;
  /**
   * Run `action` if the user can perform actions, otherwise open the trial
   * signup dialog. Safe to call regardless of `state.status` — defaults to
   * gating (open dialog) until the fetch resolves into a `ready` variant
   * with `canPerformAction === true`.
   */
  guardAction: (action: () => void) => void;
  /** Imperatively open the trial signup dialog. */
  openTrialDialog: () => void;
}

const ActionGateContext = createContext<ActionGateContextValue>({
  state: { status: "loading" },
  guardAction: () => {},
  openTrialDialog: () => {},
});

/**
 * Provider that fetches user state once and provides `guardAction` to the
 * entire app tree. Renders the TrialSignupDialog when a gated action is
 * attempted by a user without an active plan or trial.
 */
export function ActionGateProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ActionGateStatus>({ status: "loading" });
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // eslint-disable-next-line no-restricted-syntax -- one-shot app-level provider bootstrap; /api/user/state has no contract yet
        const res = await fetch("/api/user/state", { credentials: "include" });
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: "error" });
          return;
        }
        const data = (await res.json()) as { userState: UserState };
        if (cancelled) return;
        setState({ status: "ready", userState: data.userState });
      } catch {
        if (!cancelled) setState({ status: "error" });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const openTrialDialog = useCallback(() => setDialogOpen(true), []);

  const canPerformAction =
    state.status === "ready" && canPerformActionFor(state.userState);

  const guardAction = useCallback(
    (action: () => void) => {
      if (canPerformAction) {
        action();
      } else {
        setDialogOpen(true);
      }
    },
    [canPerformAction],
  );

  const value = useMemo<ActionGateContextValue>(
    () => ({ state, guardAction, openTrialDialog }),
    [state, guardAction, openTrialDialog],
  );

  return (
    <ActionGateContext.Provider value={value}>
      {children}
      <TrialSignupDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </ActionGateContext.Provider>
  );
}

/**
 * Hook that gates write/action operations behind trial or plan check.
 *
 * Returns the discriminated `state` (narrow on `state.status`) plus action
 * callbacks. For convenience, also exposes `canPerformAction` (derived
 * from `state`) and `userState` (when ready, else `null`) so the common
 * call sites don't need to switch on status.
 *
 * `guardAction` wraps any callback — if the user can perform actions
 * (subscribed, trial_active, or free_exploration) it runs immediately;
 * otherwise the trial signup dialog opens.
 */
export function useActionGate() {
  const ctx = useContext(ActionGateContext);
  const canPerformAction =
    ctx.state.status === "ready" && canPerformActionFor(ctx.state.userState);
  const userState = ctx.state.status === "ready" ? ctx.state.userState : null;
  return {
    state: ctx.state,
    guardAction: ctx.guardAction,
    openTrialDialog: ctx.openTrialDialog,
    canPerformAction,
    userState,
  };
}
